import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { researchApi, ResearchTrack } from "../../services/researchApi";

interface CreateTrackModalProps {
  open: boolean;
  onClose: () => void;
  onTrackCreated: (track: ResearchTrack) => void;
  projectId: string;
}

const CreateTrackModal: React.FC<CreateTrackModalProps> = ({
  open,
  onClose,
  onTrackCreated,
  projectId,
}) => {
  const [trackName, setTrackName] = useState("");
  const [trackType, setTrackType] = useState("find_competitors");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!trackName.trim()) return;

    try {
      setCreating(true);
      setError(null);

      const newTrack = await researchApi.createTrack({
        project: projectId,
        name: trackName.trim(),
        track_type: trackType,
      });

      // Reset form
      setTrackName("");
      setTrackType("find_competitors");

      // Notify parent and close
      onTrackCreated(newTrack);
      onClose();
    } catch (err: any) {
      console.error("Failed to create track:", err);
      setError(err.response?.data?.message || "Failed to create track");
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      setTrackName("");
      setTrackType("find_competitors");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1e1e1e",
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
        Add Research Track
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            fullWidth
            label="Track Name"
            value={trackName}
            onChange={(e) => setTrackName(e.target.value)}
            required
            autoFocus
            placeholder="e.g., European Suppliers"
            helperText="Enter a descriptive name for this research track"
          />

          <FormControl fullWidth>
            <InputLabel id="track-type-label">Track Type</InputLabel>
            <Select
              labelId="track-type-label"
              value={trackType}
              label="Track Type"
              onChange={(e) => setTrackType(e.target.value)}
            >
              <MenuItem value="find_competitors">
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Find Competitors
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Identify and analyze competitors in your space
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="find_customers">
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Find Customers & Leads
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Discover potential customers or prospects
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="find_partners">
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Find Partners & Suppliers
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Find potential partners, suppliers, or subcontractors
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!trackName.trim() || creating}
          startIcon={creating ? <CircularProgress size={16} /> : null}
        >
          {creating ? "Creating..." : "Create Track"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTrackModal;
